import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useLazyQuery, useApolloClient } from '@apollo/client';
import { Card, Col, Row } from 'react-bootstrap';
import { FlexRow, TableInMem, exportXlsModal } from 'motion-components';
import LoadingOverlay from '@achmadk/react-loading-overlay';
import gql from 'graphql-tag';
import { formatFloat } from 'shared-pages/utils/helpers';
import { Filters } from './filters';
import { ButtonExport } from 'motion-components';
import * as Excel from 'exceljs';
import saveAs from 'file-saver';
import { getCollectorById } from 'shared-pages/pages/collection-orders/graphql';

const gqlGetReportData = gql`
  query getAlarmsPerCollector(
    $startDate: DateTime!
    $endDate: DateTime!
    $collectorId: Int
  ) {
    getAlarmsPerCollector(
      startDate: $startDate
      endDate: $endDate
      collectorId: $collectorId
    ) {
      idocsCount
      idocsSumNetWeight
      alarms {
        alarmTypeId
        alarmTypeName
        count
      }
    }
  }
`;

export const AlarmsPerCollectorRep = () => {
  const client = useApolloClient();

  const [queryParams, setQueryParams] = useState({
    year: new Date().getFullYear(),
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0, 23, 59, 59),
    collectorId: null
  });

  const [getData, { loading, data: dataRaw }] = useLazyQuery(gqlGetReportData, {
    fetchPolicy: 'no-cache',
  });

  const data = dataRaw?.getAlarmsPerCollector;
  const alarms = data?.alarms;


  const filtersChanged = useCallback(
    (values) => {
      setQueryParams(values);
      getData({ variables: { ...values } });
    },
    [getData],
  );

  const fileType =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
  const fileExtension = '.xlsx';
  const wb = useRef<Excel.Workbook>(null);
  const ws = useRef<Excel.Worksheet>(null);
  const handleExport = useCallback(async () => {

    if (!wb.current) wb.current = new Excel.Workbook();
    if (!ws.current && wb.current) {
      ws.current = wb.current.addWorksheet('Αναφορά');
    }

    const boldFont = { name: 'Arial', size: 10, bold: true };
    ws.current.columns = [{ width: 40 }, { width: 10 }]

    let r = ws.current.addRow(["Φύλλο Αξιολόγησης Συλλέκτη-Μεταφορέα"], '');
    r.alignment = { vertical: 'top' };
    r.font = boldFont;

    // ---------------------------------------
    const collector = await getCollectorById(client, queryParams.collectorId)
    ws.current.addRow(["Συλλέκτης", collector?.name], '')
      .alignment = { vertical: 'top' };
    r = ws.current.addRow(["Έτος", queryParams.year], '');
    r.alignment = { vertical: 'top' };

    // ---------------------------------------
    r = ws.current.addRow([""], '');
    r = ws.current.addRow(["ΕΑΕΑ"], '');
    r.alignment = { vertical: 'top' };
    r.font = boldFont;

    r = ws.current.addRow(["Πλήθος ΕΑΕΑ:", data.idocsCount], '');
    r = ws.current.addRow(["Συνολικό Καθαρό Βάρος (kg):", data.idocsSumNetWeight], '');

    // ---------------------------------------
    r = ws.current.addRow([""], '');
    r = ws.current.addRow(["Παρατυπίες "], '');
    r.alignment = { vertical: 'top' };
    r.font = boldFont;
    data.alarms.map((alarm) => {
      r = ws.current.addRow([alarm.alarmTypeName, alarm.count], '');
      r.alignment = { wrapText: true, vertical: 'top' };
    })


    // Save File
    wb.current.xlsx.writeBuffer().then((data) => {
      console.log('saving');
      const blob = new Blob([data], { type: fileType });

      const fileName = "Αξιολόγηση Συλλέκτη" + fileExtension
        ;
      saveAs(blob, fileName);
    });

  }, [queryParams, data]);



  return (
    <Card className="shadow">
      <Card.Header>
        <FlexRow>
          <Card.Title className="m-0">Φύλλο Αξιολόγησης Συλλέκτη-Μεταφορέα</Card.Title>
        </FlexRow>
      </Card.Header>
      <Card.Body className="nopadding">
        <Filters onChange={filtersChanged} initialValues={queryParams} />
        <LoadingOverlay active={loading} spinner text="Παρακαλώ περιμένετε...">
          {!data && <div className="empty-table-container"></div>}

          {data && <div className='ml-4 mr-4 mb-4'>
            <h4 className='mt-4'>ΕΑΕΑ <small>για το έτος {queryParams.year}</small></h4>
            <Row className="pb-2" style={{ backgroundColor: "rgba(0, 0, 0, 0.05)" }}>
              <Col>Πλήθος ΕΑΕΑ:</Col><Col>{data.idocsCount}</Col>
              <Col></Col>
            </Row>
            <Row>
              <Col>Συνολικό Καθαρό Βάρος:</Col>
              <Col>
                {(data.idocsSumNetWeight ?? 0).toLocaleString(undefined, {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                })} kg
              </Col>
              <Col></Col>
            </Row>

            <h4 className='mt-4'>Παρατυπίες <small>για το έτος {queryParams.year}</small></h4>
            {data.alarms.map((alarm, idx) =>
              <Row key={idx} className="pb-2" style={{ backgroundColor: !(idx % 2) ? "rgba(0, 0, 0, 0.05)" : "white" }}>

                <Col>{alarm.alarmTypeName}:</Col>
                <Col>{alarm.count}</Col>
                <Col></Col>
              </Row>)}
            <ButtonExport className="mt-5" onClick={handleExport} />
          </div>}
        </LoadingOverlay>
      </Card.Body>
    </Card>
  );
};
